/**
 * @file auth-context.ts
 *
 * Auth context and `useAuth` hook for the admin client. Holds the bootstrap
 * status and the active auth mode resolved from the backend. The provider lives
 * in {@link ./AuthProvider}.
 */

import { createContext, useContext } from 'react';

/** Which auth flow the backend reported for this environment. */
export type AdminAuthMode = 'token' | 'password';

/**
 * Bootstrap state: `loading` until the flow resolves, then `authenticated`. In
 * password mode an unauthenticated session resolves to `unauthenticated`, which
 * renders the in-app login screen (token mode redirects instead).
 */
export type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated';

/** The logged-in caller's display identity, surfaced in the account panel. */
export interface AuthIdentitySummary {
  /** Stable subject: the username (password mode) or project UUID (token mode). */
  subject: string;
  /** Display email, when the backend carries one (password mode); UI use only. */
  email?: string;
}

export interface AuthContextValue {
  status: AuthStatus;
  mode: AdminAuthMode | null;
  /** The authenticated caller, once resolved; `null` while loading/unauthenticated. */
  identity: AuthIdentitySummary | null;
  /** Ends the session: clears the cookie/token and routes to the auth entry point. */
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

/**
 * Reads the auth context.
 *
 * @returns The current {@link AuthContextValue}.
 *
 * @throws {Error} When used outside an `AuthProvider`.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
